import { useCallback, useDeferredValue, useLayoutEffect, useMemo, useState } from "react";
import { campaignAPI } from "@/api/campaign";
import type { EngagementCreator, SaveCampaignEngagementTerms } from "@/api/engagementTerms";
import {
	getEngagementTermsMarketKey,
	loadCachedMarketCreators,
	type EngagementTermsCache,
} from "./engagementTermsCache";
import { normalizeEmail } from "@/shared/utils/email";

export const useEngagementCreators = (
	campaignId: string | null,
	marketBlockId: string | undefined,
	cache: EngagementTermsCache,
) => {
	const [creators, setCreators] = useState<EngagementCreator[]>([]);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<unknown>(null);
	const [sendTarget, setSendTarget] = useState<EngagementCreator | null>(null);
	const [sendEmail, setSendEmail] = useState("");
	const [sendMessage, setSendMessage] = useState("");
	const [sendReminder, setSendReminder] = useState(false);
	const [sending, setSending] = useState(false);
	const creatorListKey = campaignId && marketBlockId ? getEngagementTermsMarketKey(campaignId, marketBlockId) : null;

	useLayoutEffect(() => {
		let cancelled = false;
		setSelectedId(null);
		setSearch("");
		setLoadError(null);
		if (!campaignId || !marketBlockId || marketBlockId.startsWith("temp-") || !creatorListKey) {
			setCreators([]);
			return;
		}
		if (cache.marketCreators.has(creatorListKey)) {
			setCreators(cache.marketCreators.get(creatorListKey) || []);
			setLoading(false);
			return;
		}
		setCreators([]);
		setLoading(true);
		void loadCachedMarketCreators(cache, campaignId, marketBlockId)
			.then((nextCreators) => {
				if (cancelled) return;
				setCreators(nextCreators);
			})
			.catch((error) => {
				if (!cancelled) setLoadError(error);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [cache, campaignId, creatorListKey, marketBlockId]);

	const deferredSearch = useDeferredValue(search);
	const filteredCreators = useMemo(() => {
		const query = deferredSearch.trim().toLowerCase();
		return query
			? creators.filter((creator) =>
					[creator.name, creator.email, creator.handle].some((value) => value?.toLowerCase().includes(query)),
				)
			: creators;
	}, [creators, deferredSearch]);

	const selectedCreator = useMemo(
		() => creators.find((creator) => creator.assignmentId === selectedId) || null,
		[creators, selectedId],
	);
	const openSendModal = useCallback((creator: EngagementCreator | null) => {
		if (!creator) return;
		setSendTarget(creator);
		setSendEmail(creator.email || "");
		setSendMessage("");
		setSendReminder(false);
	}, []);
	const closeSendModal = useCallback(() => {
		setSendTarget(null);
		setSendEmail("");
		setSendMessage("");
		setSendReminder(false);
	}, []);
	const sendInvitation = useCallback(
		async (termsInput?: { marketBlockId: string; engagementTerms: SaveCampaignEngagementTerms }) => {
			const email = normalizeEmail(sendEmail);
			if (!campaignId || !sendTarget || !email) return null;
			const target = sendTarget;
			const message = sendMessage.trim();
			const previousEmail = target.email;
			const previousStatus = target.invitationStatus;
			const updateTarget = (
				nextEmail: string | null,
				invitationStatus: EngagementCreator["invitationStatus"],
			) => {
				setCreators((current) => {
					const next = current.map((creator) =>
						creator.assignmentId === target.assignmentId
							? { ...creator, email: nextEmail, invitationStatus }
							: creator,
					);
					if (creatorListKey) cache.marketCreators.set(creatorListKey, next);
					return next;
				});
			};

			updateTarget(email, "sent");
			closeSendModal();
			setSending(true);
			try {
				return await campaignAPI.inviteCreator(
					campaignId,
					email,
					target.assignmentId,
					message,
					termsInput,
					sendReminder,
				);
			} catch (error) {
				updateTarget(previousEmail, previousStatus);
				setSendTarget(target);
				setSendEmail(email);
				setSendMessage(message);
				throw error;
			} finally {
				setSending(false);
			}
		},
		[cache, campaignId, closeSendModal, creatorListKey, sendEmail, sendMessage, sendReminder, sendTarget],
	);
	const setCreatorCustomized = useCallback(
		(assignmentId: string, isCustomized: boolean) => {
			setCreators((current) => {
				const creator = current.find((item) => item.assignmentId === assignmentId);
				if (!creator || creator.isCustomized === isCustomized) return current;
				const next = current.map((item) =>
					item.assignmentId === assignmentId ? { ...item, isCustomized } : item,
				);
				if (creatorListKey) cache.marketCreators.set(creatorListKey, next);
				return next;
			});
		},
		[cache, creatorListKey],
	);
	const refreshCreators = useCallback(async () => {
		if (!campaignId || !marketBlockId || !creatorListKey) return;
		setLoading(true);
		setLoadError(null);
		cache.marketCreators.delete(creatorListKey);
		cache.marketCreatorsRequests.delete(creatorListKey);
		try {
			const nextCreators = await loadCachedMarketCreators(cache, campaignId, marketBlockId);
			setCreators(nextCreators);
		} catch (error) {
			setLoadError(error);
		} finally {
			setLoading(false);
		}
	}, [cache, campaignId, creatorListKey, marketBlockId]);
	return {
		creators,
		filteredCreators,
		selectedCreator,
		selectedId,
		setSelectedId,
		search,
		setSearch,
		loading,
		loadError,
		sendTarget,
		sendEmail,
		setSendEmail,
		sendMessage,
		setSendMessage,
		sendReminder,
		setSendReminder,
		sending,
		openSendModal,
		closeSendModal,
		sendInvitation,
		setCreatorCustomized,
		refreshCreators,
	};
};
